import type { NavigationAction, NavigationState } from "@-/com.npmjs.expo-router/react-navigation";
import { armReadinessDeadline } from "@-/dev.improve.haniverse.lib.readiness-handshake";

/**
 * How long Back may wait for its destination's body to report layout before it proceeds anyway.
 *
 * The wait exists only to avoid revealing a blank underlay during the pop; a healthy body lays out
 * in tens of milliseconds. The report can be lost — a skeleton fallback has no `onLayout`, the
 * body remounts under the top screen, a non-word grapheme surface never reports — and Back must
 * never be swallowed for that: after this the pop runs, and at worst the underlay paints late.
 */
export const MORPHEME_BACK_READINESS_DEADLINE_MS = 1000;

type PendingBack = {
  action: NavigationAction;
  dispatch: (action: NavigationAction) => void;
  source: string;
  target: string;
};

export function createMorphemeRouteBodyWindow() {
  const listeners = new Set<() => void>();
  const ready = new Map<string, symbol>();
  const mounted = new Map<string, symbol>();
  let pending: PendingBack | null = null;
  let disarmPendingDeadline: (() => void) | null = null;
  let version = 0;
  const notify = () => {
    version += 1;
    for (const listener of listeners) {
      listener();
    }
  };
  const clearPending = () => {
    disarmPendingDeadline?.();
    disarmPendingDeadline = null;
    pending = null;
  };
  return {
    getPendingTarget: () => pending?.target,
    getSnapshot: () => version,
    isReady: (key: string) => ready.has(key),
    markReady(key: string, epoch: symbol) {
      if (mounted.get(key) !== epoch) {
        return;
      }
      if (ready.get(key) === epoch) {
        return;
      }
      ready.set(key, epoch);
      notify();
      if (pending?.target === key) {
        const request = pending;
        clearPending();
        notify();
        request.dispatch(request.action);
      }
    },
    mount(key: string, epoch: symbol) {
      mounted.set(key, epoch);
    },
    reconcile(routeKeys: readonly string[], focusedKey: string | undefined) {
      for (const key of ready.keys()) {
        if (!routeKeys.includes(key)) {
          ready.delete(key);
        }
      }
      if (
        pending !== null &&
        (focusedKey !== pending.source || !routeKeys.includes(pending.target))
      ) {
        clearPending();
        notify();
      }
    },
    release(key: string, epoch: symbol) {
      if (mounted.get(key) !== epoch) {
        return;
      }
      mounted.delete(key);
      ready.delete(key);
      notify();
    },
    requestBack(request: PendingBack) {
      if (pending?.source === request.source && pending.target === request.target) {
        return;
      }
      clearPending();
      pending = request;
      // The layout report is the fast path; this is the guarantee that Back is never swallowed.
      disarmPendingDeadline = armReadinessDeadline({
        deadlineMs: MORPHEME_BACK_READINESS_DEADLINE_MS,
        onTimeout: () => {
          if (pending !== request) {
            return;
          }
          clearPending();
          notify();
          request.dispatch(request.action);
        },
        operation: "morpheme Back destination layout",
      });
      notify();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function getMorphemeBackDestination(
  state: NavigationState,
  action: NavigationAction,
): string | undefined {
  if (action.target !== undefined && action.target !== state.key) {
    return undefined;
  }
  let index = state.index;
  switch (action.type) {
    case "GO_BACK": {
      index -= 1;
      break;
    }
    case "POP": {
      const payload = action.payload;
      const count =
        payload !== undefined && "count" in payload && typeof payload.count === "number"
          ? payload.count
          : 1;
      index -= count;

      break;
    }
    case "POP_TO_TOP": {
      index = 0;
      break;
    }
    default: {
      return undefined;
    }
  }
  const destination = state.routes[Math.max(0, index)];
  return index < state.index && destination?.name === "[morpheme]" ? destination.key : undefined;
}
